import { useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Group } from '@cobu/api-client';
import { GroupGathering, GroupMember } from '../../store/groupDetailSlice';
import { RootState } from '../../store';
import { Strings } from '../../constants';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../utils/dateTime';
// import Logger from '@/services/Logger';

interface UseGroupInfoProps {
  group: Group;
  gatherings: GroupGathering[];
  members: GroupMember[];
  userIsGroupMember: boolean;
}

interface UseGroupInfoReturn {
  // Computed data
  pastGatherings: GroupGathering[];
  isBuildingWideGroup: boolean;
  shouldShowMembers: boolean;
  truncatedDescription: string;
  strings: any;
  currentUserId: string | null;

  // UI state
  showFullDescription: boolean;

  // Handlers
  handleShowMore: () => void;
  handleMemberPress: (member: GroupMember) => void;
  handleGatheringPress: (gathering: GroupGathering) => void;
}

/**
 * Custom hook for managing GroupInfo business logic
 * Encapsulates all state management and computed values for the GroupInfo component
 */
export const useGroupInfo = ({
  group,
  gatherings,
  members,
  userIsGroupMember,
}: UseGroupInfoProps): UseGroupInfoReturn => {
  const { user } = useSelector((state: RootState) => state.auth);
  const buildingName = user?.building?.name;
  const [showFullDescription, setShowFullDescription] = useState(false);
  const navigate = useNavigate();

  // Memoize strings to avoid repeated access
  const strings = useMemo(() => Strings.get(), []);

  // Filter past gatherings using the same logic as legacy app
  const pastGatherings = useMemo(() => {
    const now = moment();
    return gatherings.filter((gathering) => {
      const startTime = moment(gathering.scheduledStartTime);
      const thirtyMinBefore = moment(startTime).subtract(30, 'minutes');
      const sixHrsAfter = moment(startTime).add(6, 'hours');

      return !now.isBetween(thirtyMinBefore, sixHrsAfter) && !now.isBefore(thirtyMinBefore);
    }).reverse(); // Show most recent first
  }, [gatherings]);

  // Check if this is a building-wide group (matches legacy logic)
  const isBuildingWideGroup = useMemo(() => {
    return group?.isDefault ||
      group?.name === 'Marketplace' ||
      group?.name === buildingName;
  }, [group?.isDefault, group?.name, buildingName]);

  // Determine if members section should be shown (matches legacy logic)
  const shouldShowMembers = useMemo(() => {
    return members?.length > 0 &&
      !isBuildingWideGroup &&
      userIsGroupMember &&
      group.name !== buildingName;
  }, [members?.length, isBuildingWideGroup, userIsGroupMember, group.name, buildingName]);

  // Handle description truncation with same logic as legacy ShowMore component
  const truncatedDescription = useMemo(() => {
    if (!group?.description) return strings.groups.info.noDescription;
    if (showFullDescription || group?.description?.length <= 200) {
      return group?.description;
    }
    return group?.description?.substring(0, 200) + '...';
  }, [group?.description, showFullDescription, strings.groups.info.noDescription]);

  // Handle show more/less toggle
  const handleShowMore = useCallback(() => {
    setShowFullDescription(prev => !prev);
  }, []);

  // Handle member profile navigation
  const handleMemberPress = useCallback((member: GroupMember) => {
    // TODO: Implement navigation to member profile
    // Logger.log('Navigate to member profile:', member.user.uuid);
  }, []);

  // Handle gathering detail navigation
  const handleGatheringPress = useCallback((gathering: GroupGathering) => {
    // Pass eventDate to ensure timing matches between list and detail pages
    // The detail page will handle 400 errors for non-recurring events by retrying without eventDate
    const eventDate = formatDateTime(
      gathering.scheduledStartTime,
      gathering.buildingTimezone,
      'YYYY-MM-DD'
    );
    navigate({
      pathname: `/events/${gathering.uuid}`,
      search: new URLSearchParams({
        eventDate,
        currentFilter: 'past',
      }).toString(),
    });
  }, []);

  return {
    // Computed data
    pastGatherings,
    isBuildingWideGroup,
    shouldShowMembers,
    truncatedDescription,
    strings,
    currentUserId: user?.uuid || null,

    // UI state
    showFullDescription,

    // Handlers
    handleShowMore,
    handleMemberPress,
    handleGatheringPress,
  };
}; 